import type { TOC } from '@ember/component/template-only';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsCostCommissionCard from 'lumen/components/settings/cost/commission-card';
import SettingsCostDropdownsMoreActions from 'lumen/components/settings/cost/dropdowns/more-actions';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type {
  BookingWindowRow,
  CommissionMode,
  CommissionUnit,
} from 'lumen/utils/net-revenue/cost/types';

interface SettingsCostEditProfileSignature {
  Args: {
    bookingWindows: BookingWindowRow[];
    commissionValue: number | null;
    costError?: string;
    description: string;
    isSaveDisabled: boolean;
    isSaving: boolean;
    mode: CommissionMode;
    name: string;
    nameError?: string;
    onAdvancedCellChange: (
      bookingWindowId: string,
      dayIndex: number,
      value: number | null,
    ) => void;
    onCommissionValueChange: (value: number | null) => void;
    onDescriptionInput: (event: Event) => void;
    onModeChange: (mode: CommissionMode) => void;
    onNameInput: (event: Event) => void;
    onSave: () => void;
    onUnitChange: (unit: CommissionUnit) => void;
    pageTitle: string;
    unit: CommissionUnit;
  };
}

const SettingsCostEditProfile: TOC<SettingsCostEditProfileSignature> =
  <template>
    <SettingsPageTemplate
      @description={{t "settings.cost.form.description"}}
      @title={{@pageTitle}}
      @type="container"
    >
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{t "settings.cost.title"}}
            @route="market.settings.cost"
          />
          <B.item @isCurrent={{true}} @label={{@pageTitle}} />
        </PrismBreadcrumb>
      </:breadcrumb>

      <:headerAfter>
        <div class="flex flex-row items-center gap-200">
          <SettingsCostDropdownsMoreActions />
          <PrismButton
            @isDisabled={{@isSaveDisabled}}
            @isLoading={{@isSaving}}
            @label={{t "general.save_changes"}}
            @onClick={{@onSave}}
            data-test-id="cost-save-button"
          />
        </div>
      </:headerAfter>

      <:default>
        <div class="flex flex-col gap-500" data-test-id="cost-profile-form">
          <div class="flex flex-col gap-400">
            <div data-test-id="cost-name-field">
              <PrismInput
                @errorMessage={{@nameError}}
                @hasErrors={{if @nameError true false}}
                @isTouched={{if @nameError true false}}
                @label={{t "general.name"}}
                @onInput={{@onNameInput}}
                @removePadding={{true}}
                @type="text"
                @value={{@name}}
                aria-label={{t "general.name"}}
                data-test-id="cost-name-input"
              />
            </div>

            <div class="flex flex-col gap-100">
              <div class="flex items-baseline justify-between">
                <span class="paragraph-200--bold">
                  {{t "settings.cost.description.label"}}
                </span>
                <span class="text-neutral-subdued text-200--regular">
                  {{t "settings.cost.description.optional"}}
                </span>
              </div>

              <PrismInput
                @onInput={{@onDescriptionInput}}
                @placeholder={{t "settings.cost.description.placeholder"}}
                @removePadding={{true}}
                @type="text"
                @value={{@description}}
                aria-label={{t "settings.cost.description.label"}}
                data-test-id="cost-description-input"
              />

              <p class="text-neutral-subdued text-200--regular">
                {{t "settings.cost.description.note"}}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-300">
            <div class="flex flex-col gap-100">
              <h3 class="heading-4">{{t "settings.cost.costs.heading"}}</h3>
              <p class="text-neutral-subdued paragraph-300--regular">
                {{t "settings.cost.costs.description"}}
              </p>
            </div>

            <SettingsCostCommissionCard
              @bookingWindows={{@bookingWindows}}
              @commissionValue={{@commissionValue}}
              @costError={{@costError}}
              @mode={{@mode}}
              @onAdvancedCellChange={{@onAdvancedCellChange}}
              @onCommissionValueChange={{@onCommissionValueChange}}
              @onModeChange={{@onModeChange}}
              @onUnitChange={{@onUnitChange}}
              @unit={{@unit}}
            />
          </div>
        </div>
      </:default>
    </SettingsPageTemplate>
  </template>;

export default SettingsCostEditProfile;
